import { getLogger } from '@logtape/logtape';
import * as Sentry from '@sentry/react';

import {
    AVAILABILITY_ACTIONS,
    AVAILABILITY_MODES,
    type AllowedAttributes,
    CHECKIN_ENTRIES,
    CHECKIN_TYPES,
    type CategoricalAttributes,
    METRIC_FAILURE_REASONS,
    METRIC_OUTCOMES,
    METRIC_SOURCES,
    type MetricAttributeValue,
    type MetricName,
    type TenantAttributes,
} from '@/models/metrics.ts';

import { getCustomerId, getLocationKey } from '@/utils/customer-utils.ts';
import { isSalesEnvironment } from '@/utils/envUtils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';

/**
 * The single sanctioned entry point for Sentry Application Metrics (direct `Sentry.metrics.*` calls are
 * banned by ESLint). Fire-and-forget: the helpers never throw, and `Sentry.metrics` no-ops when Sentry
 * is disabled, so callers need neither try/catch nor a DSN guard.
 *
 * Tenancy is stamped here rather than passed in, so a call site cannot put the wrong id in those keys.
 */

const logger = getLogger(['lib', 'metrics']);

const toValueSet = (vocabulary: readonly MetricAttributeValue[]): ReadonlySet<MetricAttributeValue> =>
    new Set<MetricAttributeValue>(vocabulary);

const BOOLEAN_VALUES: ReadonlySet<MetricAttributeValue> = new Set<MetricAttributeValue>([true, false]);

// Tenancy ids have no fixed list of values, so they are checked by shape. No whitespace, so free text
// and names are rejected.
const MAX_IDENTIFIER_LENGTH = 64;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9_.:-]+$/;

type AttributeValidator = (value: MetricAttributeValue) => boolean;

const inVocabulary =
    (vocabulary: ReadonlySet<MetricAttributeValue>): AttributeValidator =>
    (value) =>
        vocabulary.has(value);

const isAllowedIdentifier: AttributeValidator = (value) =>
    typeof value === 'string' && value.length <= MAX_IDENTIFIER_LENGTH && IDENTIFIER_PATTERN.test(value);

// Adding a key to `AllowedAttributes` requires a validator here, or it will not compile.
const ATTRIBUTE_VALIDATORS: Record<keyof AllowedAttributes, AttributeValidator> = {
    outcome: inVocabulary(toValueSet(METRIC_OUTCOMES)),
    checkin_type: inVocabulary(toValueSet(CHECKIN_TYPES)),
    entry: inVocabulary(toValueSet(CHECKIN_ENTRIES)),
    availability_mode: inVocabulary(toValueSet(AVAILABILITY_MODES)),
    availability_action: inVocabulary(toValueSet(AVAILABILITY_ACTIONS)),
    source: inVocabulary(toValueSet(METRIC_SOURCES)),
    reason: inVocabulary(toValueSet(METRIC_FAILURE_REASONS)),
    customer_id: isAllowedIdentifier,
    location_key: isAllowedIdentifier,
    is_test: inVocabulary(BOOLEAN_VALUES),
};

// The keys the wrapper reads, derived from the validators so the two can never disagree. Anything a
// caller casts in via `any` is structurally dropped, because only these keys are ever looked up.
export const ALLOWED_ATTRIBUTE_KEYS = Object.keys(ATTRIBUTE_VALIDATORS) as readonly (keyof AllowedAttributes)[];

/** Sentry attributes take primitives only. The finite check also drops NaN and Infinity. */
const isAllowedValue = (value: unknown): value is MetricAttributeValue =>
    typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));

// Build the emitted attribute set from the closed allowlist: only allowlisted keys are read, and each
// value must be a primitive that passes that key's validator, and everything else is dropped.
const sanitizeAttributes = (attributes?: AllowedAttributes): Record<string, MetricAttributeValue> => {
    const sanitized: Record<string, MetricAttributeValue> = {};

    if (attributes) {
        for (const key of ALLOWED_ATTRIBUTE_KEYS) {
            const value = attributes[key];
            if (isAllowedValue(value) && ATTRIBUTE_VALIDATORS[key](value)) {
                sanitized[key] = value;
            }
        }
    }

    return sanitized;
};

// Blank when nothing is selected yet, which the sanitizer then drops.
const resolveTenantAttributes = (): TenantAttributes => ({
    customer_id: getCustomerId(),
    location_key: getLocationKey(),
    is_test: isSalesEnvironment(),
});

// Tenancy last, so it overwrites anything a caller cast past `CategoricalAttributes`. Called inside
// `runSafely` so a throw in one of the accessors costs the metric, not the flow being measured.
const buildAttributes = (attributes?: CategoricalAttributes): Record<string, MetricAttributeValue> =>
    sanitizeAttributes({ ...attributes, ...resolveTenantAttributes() });

/**
 * Fire-and-forget executor: swallows failures so telemetry never crashes a flow. `label` names the
 * module in the dev warning, so a domain wrapper's failures are not reported as the wrapper's own.
 */
const runSafely = (run: () => void, label: string = 'metrics'): void => {
    try {
        run();
    } catch (error) {
        if (import.meta.env.DEV) {
            logger.warn('{label} failed to emit metric', { label: label, ...serializeApiError(error) });
        }
    }
};

/** Record a counter occurrence. */
const countMetric = (name: MetricName, value: number = 1, attributes?: CategoricalAttributes): void => {
    runSafely(() => {
        // eslint-disable-next-line no-restricted-syntax
        Sentry.metrics.count(name, value, { attributes: buildAttributes(attributes) });
    });
};

/** Record a value in a statistical distribution (e.g. latency in ms). */
const distributionMetric = (
    name: MetricName,
    value: number,
    attributes?: CategoricalAttributes,
    unit: string = 'millisecond'
): void => {
    // A NaN from a missing start mark serialises to null and corrupts the distribution silently.
    if (!Number.isFinite(value)) {
        return;
    }

    runSafely(() => {
        // eslint-disable-next-line no-restricted-syntax
        Sentry.metrics.distribution(name, value, { unit: unit, attributes: buildAttributes(attributes) });
    });
};

export { countMetric, distributionMetric, runSafely };
